import { create } from 'zustand';
import type { ActiveSlot, CounterTopup, MemberSearchResult } from './api';
import { useAuthStore } from './auth-store';
import { newTopupKey } from './topup';

/**
 * The till top-up being taken (cashier phase 5), held outside React like the
 * sale store, so leaving the page and coming back keeps it, its idempotency key
 * and its in-flight flag. In memory only; sign-out drops it.
 *
 * ONE key per top-up, renewed only when a new one starts (`reset`), never when
 * an input changes. The server replays a key only for the same request and
 * answers `idempotency_key_conflict` otherwise, so after a lost response a
 * changed request cannot pass for a new one: the cashier is told an earlier
 * attempt may have gone through, checks, and starts over.
 */
export type TopupDraft = {
  member: MemberSearchResult | null;
  slots: ActiveSlot[];
  slot: ActiveSlot | null;
  amount: string;
  methodCode: string | null;
  reference: string;
  received: string;
  /** The confirm step is showing. */
  confirming: boolean;
  /**
   * A manager's approval for a large top-up, given for one slot and one
   * amount: the page drops it whenever the member, slot or amount changes.
   */
  approvalChip: string | null;
};

type TopupState = TopupDraft & {
  key: string;
  /** A request is out; a second tap waits for it. */
  charging: boolean;
  /** The finished top-up, for the slip. */
  done: CounterTopup | null;
  set: (patch: Partial<TopupDraft>) => void;
  setCharging: (charging: boolean) => void;
  finish: (done: CounterTopup) => void;
  /** Start a new top-up: empty form, new key. */
  reset: () => void;
};

const emptyDraft = (): TopupDraft => ({
  member: null,
  slots: [],
  slot: null,
  amount: '',
  methodCode: null,
  reference: '',
  received: '',
  confirming: false,
  approvalChip: null,
});

export const useTopupStore = create<TopupState>((set) => ({
  ...emptyDraft(),
  key: newTopupKey(),
  charging: false,
  done: null,
  set: (patch) => set(patch),
  setCharging: (charging) => set({ charging }),
  finish: (done) => set({ done, charging: false, confirming: false }),
  reset: () => set({ ...emptyDraft(), key: newTopupKey(), charging: false, done: null }),
}));

useAuthStore.subscribe((auth, prev) => {
  if (prev.token && !auth.token) useTopupStore.getState().reset();
});
